import type { ReactNode } from 'react';
import { Sheet as TamaguiSheet } from 'tamagui';
import { borderWidth } from '@/theme/tokens.generated';

// Issue #150 Task 5. Tamagui's own `Sheet` (bundled as a real
// dependency of the `tamagui` package -- `tamagui`'s own root export
// re-exports `@tamagui/sheet` wholesale, confirmed by reading
// node_modules/tamagui/dist/esm/index.mjs directly) already ships a
// complete, cross-platform bottom-sheet implementation: drag-to-dismiss,
// snap points, an overlay, keyboard-avoidance -- exactly the kind of
// thing this project's own "rely on the ecosystem, don't hand-roll"
// principle (tamagui.config.ts's own header comment) says to reach for
// rather than reimplement. This is a thin, TOKENIZED wrapper around it,
// not a from-scratch sheet.
//
// No current real call site needs one -- grepped every screen for an
// existing modal-like/bottom-sheet pattern; the one `Modal` hit
// (sign-up/index.tsx) is an Expo Router route-level modal presentation,
// a different mechanism entirely, not an in-page overlay this
// component would replace. Per this task's own scope, building the
// primitive correctly is the job here; migrating a future confirmation
// flow (e.g. a Block/Report confirm-before-submit step) to use it is
// that future screen's own call, not retrofitted here.
export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  // Tamagui's own snapPoints prop, passed straight through -- percent-
  // of-screen-height values, e.g. [50] for "half the screen". Defaults
  // to a single full-content snap point (Tamagui's own default) when
  // omitted, correct for a short confirmation-style sheet.
  snapPoints?: number[];
}

export function Sheet({ open, onOpenChange, children, snapPoints }: SheetProps) {
  return (
    <TamaguiSheet
      modal
      open={open}
      onOpenChange={onOpenChange}
      snapPoints={snapPoints}
      // "medium", not "quick"/"lazy" -- this app's own motion driver
      // (#146/#195) preset naming; medium matches tokens/motion.json's
      // `transition.default` (this project's stated general-purpose
      // state-change pace), the right register for something as
      // consequential as a sheet opening/closing, faster than "lazy"
      // (reserved for larger, slower transitions per that preset's own
      // tamagui.config.ts comment) but not as snappy as "quick" (tuned
      // for hover/press micro-interactions, too abrupt for a sheet).
      dismissOnSnapToBottom
      transition="medium"
    >
      <TamaguiSheet.Overlay
        transition="medium"
        enterStyle={{ opacity: 0 }}
        exitStyle={{ opacity: 0 }}
        backgroundColor="$shadowColor"
      />
      <TamaguiSheet.Handle backgroundColor="$borderColorStrong" />
      {/* Same warm/soft material recipe as Card's own cardStyle
          (src/components/card.tsx) -- a sheet is real surface content,
          not a system-chrome element, and should read as "the same
          product" as every card already on screen. Sharp corners on
          top only (borderTopLeftRadius/borderTopRightRadius, not the
          full $xl radius) since a sheet's bottom edge is off-screen. */}
      <TamaguiSheet.Frame
        backgroundColor="$card"
        borderTopLeftRadius="$xl"
        borderTopRightRadius="$xl"
        borderWidth={borderWidth.hairline}
        borderColor="$borderColor"
        padding="$4"
        gap="$3"
      >
        {children}
      </TamaguiSheet.Frame>
    </TamaguiSheet>
  );
}
